import { api } from '../api/client';
import type { UploadStatus } from '../api/hooks';

/**
 * putToS3 — raw XMLHttpRequest PUT to a presigned S3 URL, with upload-progress
 * events. Used by the Content media UploadModal and the Releases artifact-upload
 * modal. XHR (not fetch/api()) because: a presigned URL must NOT carry the
 * Bearer header, and XHR gives `upload.progress` events (large .mender uploads
 * need a progress bar). Content-Type is left to the browser (the presign doesn't
 * pin it), so no header needs to match the signature.
 */
export function putToS3(url: string, file: File, onProgress: (pct: number) => void): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
    });
    xhr.addEventListener('load', () =>
      xhr.status >= 200 && xhr.status < 300
        ? resolve()
        : reject(new Error(`S3 responded ${xhr.status}`)));
    xhr.addEventListener('error', () => reject(new Error('network error during upload')));
    xhr.addEventListener('abort', () => reject(new Error('upload cancelled')));
    xhr.open('PUT', url);
    xhr.send(file);
  });
}


/**
 * pollUpload — wait for the content-processor Lambda to reach a terminal state.
 *
 * The upload itself only puts bytes in S3; registration happens asynchronously
 * when the Lambda fires, so "the PUT succeeded" says nothing about whether the
 * asset exists. Every caller that needs to act on the registered asset — the
 * upload modal, and attaching a subtitle to an item — has to wait for this.
 */
const TERMINAL_UPLOAD_STATES = ['pending-confirmation', 'done', 'failed', 'cancelled'];

export async function pollUpload(id: string, max = 300): Promise<UploadStatus | null> {
  let last: UploadStatus | null = null;
  for (let i = 0; i < max; i++) {
    await new Promise((r) => setTimeout(r, 2000));
    try {
      const s = await api<UploadStatus>(`/content/uploads/${encodeURIComponent(id)}`);
      last = s;
      if (TERMINAL_UPLOAD_STATES.includes(s.status)) return s;
    } catch { /* transient — keep polling */ }
  }
  return last;
}
